import React, { useEffect, useState } from 'react';
import { ArrowDownNarrowWide, ChevronDown, ChevronUp, UsersRound } from 'lucide-react';
import ClientRow from './TeamClientRow';
import TeamClientFilters from './TeamClientFilters';
import Pagination from '../../../components/ui/pagination/Pagination';
import { camelizeKeys } from '../../../utils/camelizeKeys';
import { snakecaseKeys } from '../../../utils/snakecaseKeys';
import { getStoredHeaderPlannerId } from '../../../utils/headerPlannerSelection';

const TeamClients = ({
  clients: initialClients,
  pagination: initialPagination,
  assessors: initialAssessors,
  currentAdmin,
}) => {
  const [clients, setClients] = useState(initialClients);
  const [pagination, setPagination] = useState(initialPagination);
  const [assessors] = useState(initialAssessors);

  const initialPlannerId = (() => {
    const stored = getStoredHeaderPlannerId(currentAdmin?.id);
    if (!stored) return '';
    const validIds = (initialAssessors || []).map((a) => String(a.id));
    return validIds.includes(String(stored)) ? stored : '';
  })();

  const [filters, setFilters] = useState({
    search: '',
    status: '',
    plannerId: initialPlannerId,
    walletProfile: '',
  });
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  const buildQuery = (params) => {
    const snakeParams = snakecaseKeys(params);

    return Object.entries(snakeParams)
      .filter(([, v]) => v !== '')
      .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
      .join('&');
  };

  const fetchData = (page = 1, filterParams = filters, sort = sortBy, order = sortOrder) => {
    const query = buildQuery({ ...filterParams, page, sort, order });

    fetch(`/app/planner/team_clients?${query}`)
      .then((res) => res.text())
      .then((html) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const data = doc.querySelector('#app-root')?.dataset?.props;
        const parsed = camelizeKeys(JSON.parse(data));

        setClients(parsed.clients);
        setPagination(parsed.pagination);
      });
  };

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    fetchData(1, newFilters);
  };

  useEffect(() => {
    if (initialPlannerId) {
      fetchData(1, { ...filters, plannerId: initialPlannerId });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePageChange = (newPage) => {
    fetchData(newPage);
  };

  const handleSortClick = (field) => {
    const newOrder = sortBy === field && sortOrder === 'asc' ? 'desc' : 'asc';
    setSortBy(field);
    setSortOrder(newOrder);
    fetchData(1, filters, field, newOrder);
  };

  const handleOrderToggle = () => {
    const nextSortBy = sortBy === 'name' ? 'updated_at' : 'name';
    const nextSortOrder = nextSortBy === 'name' ? 'asc' : 'desc';
    setSortBy(nextSortBy);
    setSortOrder(nextSortOrder);
    fetchData(1, filters, nextSortBy, nextSortOrder);
  };

  const getColumnLabel = (column) => {
    switch (column) {
      case 'name':
        return 'nome';
      case 'updated_at':
        return 'última atualização';
      case 'status':
        return 'status';
      default:
        return '';
    }
  };

  const renderSortIcon = (field) => {
    if (sortBy !== field) return null;
    return sortOrder === 'asc' ? (
      <ChevronUp size={14} className="text-tealMedium ml-1" />
    ) : (
      <ChevronDown size={14} className="text-tealMedium ml-1" />
    );
  };

  return (
    <div className="h-full flex flex-col gap-4">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <h2 className="px-4 text-2xl font-semibold text-blueDarker flex items-center gap-2 font-jost">
          <UsersRound strokeWidth={1.25} className="w-6 h-6 text-tealMedium" />
          Equipe
        </h2>

        <TeamClientFilters filters={filters} onChange={handleFilterChange} assessors={assessors} />
      </div>

      <table className="w-full text-left table-fixed">
        <thead>
          <tr>
            <th className="pl-4 py-2 w-1/12">
              <button
                onClick={() => handleSortClick('status')}
                className="flex items-center hover:opacity-80 text-blueDarker font-semibold text-base"
              >
                Status
                {renderSortIcon('status')}
              </button>
            </th>
            <th className="py-2 w-1/4">
              <button
                onClick={() => handleSortClick('name')}
                className="flex items-center hover:opacity-80 text-blueDarker font-semibold text-base"
              >
                Nome
                {renderSortIcon('name')}
              </button>
            </th>
            <th className="pr-4 py-2 w-1/6">
              <span className="text-blueDarker font-semibold text-base">Planejador</span>
            </th>
            <th className="pl-6 py-2 w-1/6">
              <button
                onClick={() => handleSortClick('updated_at')}
                className="flex items-center hover:opacity-80 text-blueDarker font-semibold text-base"
              >
                Última atualização
                {renderSortIcon('updated_at')}
              </button>
            </th>
            <th className="pr-4 py-2" style={{ width: '380px', minWidth: '360px' }}>
              <div className="flex justify-end">
                <button onClick={handleOrderToggle} className="flex items-center hover:opacity-80 gap-1">
                  <ArrowDownNarrowWide strokeWidth={1.5} size={20} className="text-tealMedium" />
                  <span className="text-blueDarker text-base">
                    <span className="font-semibold">Ordenar por:</span> {getColumnLabel(sortBy)}
                  </span>
                </button>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          {clients.map((client, index) => (
            <ClientRow key={client.id} client={client} index={index} />
          ))}
        </tbody>
      </table>

      <Pagination
        currentPage={pagination.currentPage}
        totalPages={pagination.totalPages}
        onPageChange={handlePageChange}
      />
    </div>
  );
};

export default TeamClients;
